import { HTTP } from '@awesome-cordova-plugins/http';
import { Capacitor } from '@capacitor/core';
import { ToastProps } from '@chakra-ui/react';
import { CapacitorWifiConnect } from '@falconeta/capacitor-wifi-connect';

import { WifiNetwork } from '../../../common/components/WifiNetworkList';
import { timeout } from '../../../utils/common/common-utils';
import { FeatureFlag, getFlagValue } from '../../../utils/common/feature-flag-helpers';
import { get, getWithStatus, post, put } from '../../account/hardware/api-helpers';
import { BASE_IPV4_ADDRESS, EVSE_API_USERNAME, EVSE_FACTORY_PIN_CODE } from '../constants';
import { EvseProductInformation, EVSEToken, WifiData, WifiListNetwork } from '../types';

const EVSE_API_URL = `${BASE_IPV4_ADDRESS}/evse/v1`;
const WIFI_MAX_RETRIES = 6;
const WIFI_RETRY_DELAY_MS = 1000;
const LOGIN_REQUEST_TIMEOUT_MS = 10000;
// Time to wait for a 200 OK from the PUT before treating it as a timeout.
const PUT_WIFI_RESPONSE_TIMEOUT_MS = 3000;
// Fixed settle period after PUT before issuing the first GET — allows the charger
// to disconnect from the old network and begin joining the new one.
export const WIFI_SWITCH_SETTLE_MS = 10000;
// Per-request timeout for each GET /evsemanager/methods/wifi poll.
const WIFI_STATUS_REQUEST_TIMEOUT_MS = 6000;
// Total window over which to keep polling on 500 responses.
const WIFI_STATUS_POLL_WINDOW_MS = 60000;
const EVSE_REACHABILITY_TIMEOUT_MS = 5000;
const EVSE_REACHABILITY_MAX_ATTEMPTS = 3;
const isLoggingEnabled = getFlagValue(FeatureFlag.EnableEVSELogs);

const isNativePlatform = () => Capacitor.isNativePlatform();

// Cordova HTTP trust mode is global, so only restore it once the last overlapping local request settles.
let localTlsTrustUsers = 0;

async function withLocalTlsTrust<T>(url: string, requestFn: () => Promise<T>): Promise<T> {
  const isLocalCharger = url.includes(BASE_IPV4_ADDRESS.split('//')[1]);

  if (!isLocalCharger || !isNativePlatform()) {
    return requestFn();
  }

  localTlsTrustUsers += 1;
  try {
    await HTTP.setServerTrustMode('nocheck');
    return await requestFn();
  } finally {
    localTlsTrustUsers -= 1;
    if (localTlsTrustUsers === 0) await HTTP.setServerTrustMode('default');
  }
}

function throwIfAborted(signal?: AbortSignal) {
  if (signal?.aborted) throw new Error('EVSE request aborted');
}

/**
 * Authenticates with the EVSE Charger using the provided password and retrieves an authentication token.
 *
 * @param password - The password used to authenticate with the EVSE Charger.
 * @param signal - Stops further login attempts once aborted.
 * @returns A promise that resolves to the authentication token as a string.
 * @throws Error if authentication fails or the token is not returned.
 */
export async function getAuthToken(
  password: string,
  toast: (props: ToastProps) => void,
  signal?: AbortSignal
): Promise<string> {
  isLoggingEnabled &&
    toast({
      title: 'DBG: Sending login request',
      description: `Data: ${JSON.stringify({ username: EVSE_API_USERNAME, password })}`,
      status: 'info',
      isClosable: true,
      duration: 8000,
    });
  let response;
  // Retry logic for Wi-Fi connection errors.
  for (let attempt = 1; attempt <= WIFI_MAX_RETRIES; attempt++) {
    throwIfAborted(signal);
    try {
      const timeoutPromise = timeout(LOGIN_REQUEST_TIMEOUT_MS).then(() => {
        throw new Error('EVSE login request timed out');
      });
      response = await Promise.race([
        withLocalTlsTrust(`${EVSE_API_URL}/login/login`, () =>
          post<EVSEToken>(
            `${EVSE_API_URL}/login/login`,
            {
              username: EVSE_API_USERNAME,
              password,
            },
            { 'Content-Type': 'application/json' }
          )
        ).then((data) => ({ data, status: 200 })),
        timeoutPromise,
      ]);
      break; // Success, exit retry loop.
    } catch (error) {
      const errorMessage = [
        (error as { message?: string })?.message,
        (error as { errorMessage?: string })?.errorMessage,
        // Cordova HTTP plugin throws { status, error } objects — check the `error` property too.
        (error as { error?: string })?.error,
        String(error),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const isHttpResponseError = errorMessage.includes('http ');
      const isWiFiError =
        !isHttpResponseError &&
        /offline|network error|failed to fetch|timed out|timeout|network request failed|load failed/.test(errorMessage);

      if (isWiFiError && attempt < WIFI_MAX_RETRIES) {
        await timeout(WIFI_RETRY_DELAY_MS);
        continue; // Retry.
      }

      // Either not a Wi-Fi error, or we've exhausted retries.
      throw error;
    }
  }
  isLoggingEnabled &&
    toast({
      title: 'DBG: Login request sent',
      description: `Response: ${JSON.stringify(response)}`,
      status: 'info',
      isClosable: true,
      duration: 8000,
    });

  if (!response?.data?.token) throw Error('Failed to login to the EVSE Charger');
  return response.data.token;
}

/**
 * Fetch for device product information from the EVSE API.
 */
export async function fetchDeviceProductInfo(authToken: string, signal?: AbortSignal): Promise<EvseProductInformation> {
  throwIfAborted(signal);
  const url = `${EVSE_API_URL}/productinformation`;
  const response = await withLocalTlsTrust(url, () =>
    getWithStatus<EvseProductInformation>(url, {
      Accept: 'application/json',
      Authorization: authToken,
    })
  );

  if (response.status !== 200) {
    throw new Error(`Failed to fetch device product information. Status: ${response.status}`);
  }

  return response.data;
}

async function probeEvseAp(password: string): Promise<boolean> {
  const url = `${EVSE_API_URL}/login/login`;
  try {
    const data = await withLocalTlsTrust(url, async () => {
      const body = { username: EVSE_API_USERNAME, password };
      const headers = { 'Content-Type': 'application/json' };
      if (isNativePlatform()) {
        const response = await HTTP.sendRequest(url, {
          method: 'post',
          data: body,
          headers,
          serializer: 'json',
          timeout: EVSE_REACHABILITY_TIMEOUT_MS / 1000,
        });
        return JSON.parse(response.data) as EVSEToken;
      }
      return Promise.race([
        post<EVSEToken>(url, body, headers),
        timeout(EVSE_REACHABILITY_TIMEOUT_MS).then(() => undefined),
      ]);
    });
    return !!data?.token;
  } catch {
    return false;
  }
}

export async function isEvseApReachable(): Promise<boolean> {
  const maxAttempts = isNativePlatform() ? EVSE_REACHABILITY_MAX_ATTEMPTS : 1;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (await probeEvseAp(EVSE_FACTORY_PIN_CODE)) return true;
    if (attempt < maxAttempts - 1) await timeout(WIFI_RETRY_DELAY_MS);
  }
  return false;
}

/**
 * Fetch for nearby Wi-Fi networks (from the POV of the wi-fi device we are connected to).
 * Polling until the networks are ready or the maximum number of attempts is reached.
 *
 * @param authToken - The authorization token used for authorization in the request header.
 * @param maxAttempts - The maximum number of polling attempts before giving up. Defaults to 10.
 * @param pollDelayMs - The delay in milliseconds between polling attempts. Defaults to 1000 ms.
 * @returns An array of Wi-Fi networks.
 */
export async function fetchWifiNetworks(
  authToken: string,
  _toast: (props: ToastProps) => void,
  maxAttempts = 20,
  pollDelayMs = 1000
): Promise<WifiNetwork[]> {
  const url = `${EVSE_API_URL}/getwifilist`;
  let response;
  let attempts = 0;

  do {
    response = await withLocalTlsTrust(url, () =>
      getWithStatus<WifiListNetwork[] | string>(url, {
        Accept: 'application/json',
        Authorization: authToken,
      })
    );

    // 202 = scan still in progress on the charger.
    // 200 with a non-array body = scan not yet populated (charger returns a string
    // or empty body on first call before it has built the network list).
    const scanNotReady = response.status === 202 || (response.status === 200 && !Array.isArray(response.data));
    if (!scanNotReady) break;
    attempts++;
    await timeout(pollDelayMs);
  } while (attempts < maxAttempts);

  if (response.status === 202 || (response.status === 200 && !Array.isArray(response.data))) {
    throw new Error('Wi-Fi network list not ready after maximum polling attempts');
  }

  if (response.status !== 200) {
    throw new Error(`Failed to fetch Wi-Fi networks. Status: ${response.status}`);
  }

  if (!Array.isArray(response.data)) {
    throw new Error('Invalid Wi-Fi network data format');
  }

  return dedupeBySsid(response.data);
}

/**
 * Collapses a charger Wi-Fi scan to one entry per SSID, keeping the strongest signal.
 *
 * A mesh network advertises the same SSID from every node, so an unfiltered scan shows the
 * same network several times and the user cannot tell the entries apart (SHN-3018).
 * Blank SSIDs are dropped; the result is sorted strongest-first.
 */
export function dedupeBySsid(networks: WifiListNetwork[]): WifiNetwork[] {
  const strongestBySsid = new Map<string, WifiNetwork>();

  for (const { ssid, signalStrength } of networks) {
    if (!ssid?.trim()) continue;
    const existing = strongestBySsid.get(ssid);
    if (!existing || signalStrength > existing.signal) {
      strongestBySsid.set(ssid, { ssid, signal: signalStrength, security: [] });
    }
  }

  return [...strongestBySsid.values()].sort((a, b) => b.signal - a.signal);
}

/**
 * Set a specific Wi-Fi network to the device.
 *
 * @param wifiData - An object containing the WiFi SSID and password.
 * @param authToken - The authorization token used for authorization in the request header.
 * @returns A promise that resolves when the WiFi settings have been successfully updated.
 */
export async function putWifiSettings(
  wifiData: WifiData,
  authToken: string,
  _toast: (props: ToastProps) => void
): Promise<void> {
  const url = `${EVSE_API_URL}/wificonnectivitysettings`;
  // Spec: wait up to 3 seconds for a 200 OK from the PUT.
  await Promise.race([
    withLocalTlsTrust(url, () =>
      put(
        url,
        {
          ssid: wifiData.ssid,
          securityOption: true,
          wifiPasscode: wifiData.password,
        },
        {
          Authorization: authToken,
          'Content-Type': 'application/json',
        }
      )
    ),
    timeout(PUT_WIFI_RESPONSE_TIMEOUT_MS).then(() => {
      throw new Error('PUT wificonnectivitysettings timed out');
    }),
  ]);
}

/**
 * Fetches the current Wi-Fi settings from the device.
 *
 * @param authToken - The authorization token used for authorization in the request header.
 * @param toast - A function to display toast notifications.
 * @returns A promise that resolves to an object containing the current Wi-Fi settings SSID.
 */
export async function getWifiSettings(
  authToken: string,
  _toast: (props: ToastProps) => void
): Promise<{ ssid: string }> {
  const url = `${EVSE_API_URL}/wificonnectivitysettings`;
  const response = await withLocalTlsTrust(url, () =>
    get<{ ssid: string }>(url, {
      Authorization: authToken,
      'Content-Type': 'application/json',
    })
  );
  return { ssid: response.ssid };
}

/**
 * Checks if the device is connected to Wi-Fi after applying new settings.
 *
 * Spec:
 *   - Each GET /evsemanager/methods/wifi is subject to a 6-second request timeout.
 *   - 200 OK → charger has connected; return true immediately.
 *   - 500 or timeout → charger is still switching; wait 6 s then retry.
 *   - After 60 seconds of polling without a 200, return false.
 *
 * @param authToken - The authorization token used for authorization in the request header.
 * @param _toast - Unused; kept for API consistency.
 * @param pollWindowMs - Total polling window in ms. Defaults to 60 000 ms.
 * @param requestTimeoutMs - Per-request timeout in ms. Defaults to 6 000 ms.
 * @returns A promise that resolves to a boolean indicating whether the device is connected to Wi-Fi.
 */
export async function isConnectedToWifi(
  authToken: string,
  _toast: (props: ToastProps) => void,
  pollWindowMs = WIFI_STATUS_POLL_WINDOW_MS,
  requestTimeoutMs = WIFI_STATUS_REQUEST_TIMEOUT_MS
): Promise<boolean> {
  const deadline = Date.now() + pollWindowMs;
  const url = `${EVSE_API_URL}/evsemanager/methods/wifi`;

  do {
    try {
      const response = await Promise.race([
        withLocalTlsTrust(url, () =>
          getWithStatus<{
            ssid: string;
            status: 'disconnected' | 'connecting' | 'connected' | 'error';
          }>(url, {
            Authorization: authToken,
            'Content-Type': 'application/json',
          })
        ),
        timeout(requestTimeoutMs).then(() => {
          throw new Error('GET wifi status timed out');
        }),
      ]);

      // 200 OK means the charger has successfully joined the new network.
      if (response.status === 200) {
        return true;
      }
      // Any non-200 (e.g. 500) means the charger is still switching — continue polling.
    } catch (_error) {
      // Request timed out or threw — continue polling until the window expires.
    }

    if (Date.now() < deadline) {
      await timeout(requestTimeoutMs);
    }
  } while (Date.now() < deadline);

  // 60-second window exhausted without a 200 OK.
  return false;
}

/**
 * Attempts to connect the mobile device to the selected home Wi-Fi network and verify internet connectivity.
 *
 * Android:
 * - Will disconnect from the EVSE AP if needed and reconnect to the home network.
 *
 * iOS:
 * - Disconnecting from the EVSE AP is ineffective.
 * - If iOS reconnects to the EVSE AP, the function throws an error.
 *
 * @param currentSsid - The SSID of the currently connected Wi-Fi network.
 * @param homeSsid - The SSID of the home Wi-Fi network.
 * @param password - Optional password for the home Wi-Fi network.
 */
export async function connectToSelectedNetwork(currentSsid: string, homeSsid: string, password?: string) {
  const isOnHomeSsid = currentSsid === homeSsid;

  // Force connect only if not already on the desired home network.
  if (!isOnHomeSsid) {
    const attemptConnect = async () => {
      if (password) {
        await CapacitorWifiConnect.secureConnect({
          ssid: homeSsid,
          password,
          saveNetwork: false,
        });
      } else {
        await CapacitorWifiConnect.connect({ ssid: homeSsid });
      }
    };

    try {
      await attemptConnect();
    } catch (e) {
      // Verify current SSID.
      const current = await CapacitorWifiConnect.getDeviceSSID();
      const currentSsid = current?.value;
      if (currentSsid !== homeSsid) {
        await attemptConnect();
      }
    }

    await timeout(3000);
  }
}
